/**
 * networkDrafts.js
 * Lightweight safety net for when /api/generate fails due to network issues
 * (offline, timeout, DNS). Stashes the notes + metadata to localStorage so
 * nothing is lost while the provider is on a flaky cell signal.
 *
 * NOT a replacement for true offline mode — just a crash-safety buffer.
 */

const KEY = 'writeright_network_drafts';
const MAX_DRAFTS = 25;

export const isNetworkError = (err) => {
  if (!err) return false;
  // Axios: no response + no status means the request never completed
  if (err.response) return false;
  if (err.code === 'ECONNABORTED') return true; // timeout
  if (err.code === 'ERR_NETWORK') return true;
  if (err.message && /network|timeout|failed to fetch/i.test(err.message)) return true;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  return false;
};

const readRaw = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeRaw = (arr) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(arr.slice(0, MAX_DRAFTS)));
  } catch { /* storage full or unavailable */ }
};

export const stashNetworkDraft = ({ source, notes, meta, label }) => {
  if (!notes || !notes.trim()) return null;
  const entry = {
    id: `nd_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    source: source || 'unknown', // 'standard' | 'speed' | 'patient'
    label: label || '',
    notes,
    meta: meta || {},
    savedAt: new Date().toISOString(),
  };
  const existing = readRaw();
  writeRaw([entry, ...existing]);
  // Let listeners (banner, etc) refresh
  window.dispatchEvent(new CustomEvent('writeright:network-drafts-changed'));
  return entry;
};

export const getNetworkDrafts = () => readRaw();

export const removeNetworkDraft = (id) => {
  const next = readRaw().filter((d) => d.id !== id);
  writeRaw(next);
  window.dispatchEvent(new CustomEvent('writeright:network-drafts-changed'));
};

export const clearNetworkDrafts = () => {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent('writeright:network-drafts-changed'));
};
